<script>
// NOTICE: KAPTION-INTEGRATION
// WhatsApp Coexistence History Sync monitoring page
// Shows sync progress for 6-month historical data sync
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from '../../../../../api/inboxes';

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      syncData: null,
      isLoading: true,
      isTriggering: false,
      refreshInterval: null,
    };
  },
  computed: {
    statusLabel() {
      const statusMap = {
        pending: this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS.PENDING'),
        in_progress: this.$t(
          'INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS.IN_PROGRESS'
        ),
        completed: this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS.COMPLETED'),
        failed: this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS.FAILED'),
        declined: this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS.DECLINED'),
      };
      return statusMap[this.syncData?.status] || this.syncData?.status;
    },
    statusClass() {
      const classMap = {
        pending: 'bg-slate-100 text-slate-700',
        in_progress: 'bg-woot-100 text-woot-700',
        completed: 'bg-green-100 text-green-700',
        failed: 'bg-red-100 text-red-700',
        declined: 'bg-yellow-100 text-yellow-700',
      };
      return classMap[this.syncData?.status] || '';
    },
    isCoexistence() {
      return this.syncData?.is_coexistence || false;
    },
    overallProgress() {
      return this.syncData?.progress?.overall || 0;
    },
    canTriggerTranslation() {
      return (
        this.syncData?.status === 'completed' &&
        !this.syncData?.translation?.is_complete &&
        this.syncData?.translation?.pending === 0
      );
    },
    hasTranslationInProgress() {
      return (
        this.syncData?.translation?.pending > 0 &&
        this.syncData?.translation?.completed > 0
      );
    },
  },
  mounted() {
    this.fetchSyncStatus();
    // Auto-refresh every 10 seconds if sync is in progress
    this.refreshInterval = setInterval(() => {
      if (
        this.syncData?.status === 'in_progress' ||
        this.hasTranslationInProgress
      ) {
        this.fetchSyncStatus();
      }
    }, 10000);
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async fetchSyncStatus() {
      try {
        const response = await InboxesAPI.getWhatsappHistorySync(this.inbox.id);
        this.syncData = response.data;
      } catch {
        // Error handled silently - sync status will show loading state
      } finally {
        this.isLoading = false;
      }
    },
    async triggerTranslation() {
      this.isTriggering = true;
      try {
        await InboxesAPI.triggerWhatsappHistoryTranslation(this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_TRIGGERED')
        );
        this.fetchSyncStatus();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_ERROR'));
      } finally {
        this.isTriggering = false;
      }
    },
    formatNumber(num) {
      return (num || 0).toLocaleString();
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString();
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="spinner" />
      <span class="ml-2 text-slate-500">{{
        $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.LOADING')
      }}</span>
    </div>

    <template v-else>
      <!-- Not Coexistence Warning -->
      <div
        v-if="!isCoexistence"
        class="p-4 mb-6 rounded-lg bg-yellow-50 border border-yellow-200"
      >
        <p class="text-yellow-800">
          {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.NOT_COEXISTENCE') }}
        </p>
      </div>

      <!-- Sync Status Section -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TITLE')"
        :sub-title="$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DESCRIPTION')"
      >
        <div class="space-y-6">
          <!-- Status Badge -->
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-slate-600">{{
              $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS_LABEL')
            }}</span>
            <span
              class="px-3 py-1 text-sm font-medium rounded-full"
              :class="statusClass"
            >
              {{ statusLabel }}
            </span>
          </div>

          <!-- Error Message -->
          <div
            v-if="syncData?.error"
            class="p-4 rounded-lg bg-red-50 border border-red-200"
          >
            <p class="text-sm text-red-700">
              <strong>
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.ERROR') }}:
              </strong>
              {{ syncData.error }}
              <span v-if="syncData.error_code" class="text-red-500">
                ({{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.ERROR_CODE') }}:
                {{ syncData.error_code }})
              </span>
            </p>
          </div>

          <!-- Progress Bar -->
          <div v-if="syncData?.status === 'in_progress'" class="space-y-2">
            <div class="flex justify-between text-sm text-slate-600">
              <span>{{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.OVERALL_PROGRESS')
              }}</span>
              <span>{{ overallProgress }}%</span>
            </div>
            <div class="w-full h-2 rounded-full bg-slate-200">
              <div
                class="h-2 rounded-full bg-woot-500 transition-all duration-300"
                :style="{ width: `${overallProgress}%` }"
              />
            </div>
          </div>

          <!-- Stats Grid -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <!-- Messages Synced -->
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.MESSAGES_SYNCED') }}
              </p>
              <p class="text-2xl font-semibold text-slate-900 mt-1">
                {{ formatNumber(syncData?.progress?.messages_synced) }}
              </p>
            </div>

            <!-- Conversations Synced -->
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{
                  $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.CONVERSATIONS_SYNCED')
                }}
              </p>
              <p class="text-2xl font-semibold text-slate-900 mt-1">
                {{ formatNumber(syncData?.progress?.conversations_synced) }}
              </p>
            </div>

            <!-- Attachments -->
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.ATTACHMENTS') }}
              </p>
              <p class="text-2xl font-semibold text-slate-900 mt-1">
                {{ formatNumber(syncData?.attachments?.completed) }}
                <span class="text-sm text-slate-500">
                  / {{ formatNumber(syncData?.attachments?.pending) }}
                </span>
              </p>
            </div>

            <!-- Transcriptions -->
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSCRIPTIONS') }}
              </p>
              <p class="text-2xl font-semibold text-slate-900 mt-1">
                {{ formatNumber(syncData?.transcription?.completed) }}
                <span class="text-sm text-slate-500">
                  / {{ formatNumber(syncData?.transcription?.pending) }}
                </span>
              </p>
            </div>
          </div>

          <!-- Timestamps -->
          <div class="flex flex-wrap gap-6 text-sm text-slate-500">
            <div v-if="syncData?.timestamps?.started_at">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STARTED_AT') }}:
              </span>
              {{ formatDate(syncData.timestamps.started_at) }}
            </div>
            <div v-if="syncData?.timestamps?.completed_at">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.COMPLETED_AT') }}:
              </span>
              {{ formatDate(syncData.timestamps.completed_at) }}
            </div>
          </div>
        </div>
      </SettingsSection>

      <!-- Translation Section -->
      <SettingsSection
        v-if="syncData?.status === 'completed'"
        :title="$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_DESCRIPTION')
        "
      >
        <div class="space-y-4">
          <!-- Translation Stats -->
          <div class="grid grid-cols-3 gap-4">
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_PENDING') }}
              </p>
              <p class="text-2xl font-semibold text-slate-900 mt-1">
                {{ formatNumber(syncData?.translation?.pending) }}
              </p>
            </div>
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{
                  $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_COMPLETED')
                }}
              </p>
              <p class="text-2xl font-semibold text-green-600 mt-1">
                {{ formatNumber(syncData?.translation?.completed) }}
              </p>
            </div>
            <div class="p-4 rounded-lg bg-slate-50">
              <p class="text-xs text-slate-500 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_FAILED') }}
              </p>
              <p class="text-2xl font-semibold text-red-600 mt-1">
                {{ formatNumber(syncData?.translation?.failed) }}
              </p>
            </div>
          </div>

          <!-- Translation Status -->
          <div
            v-if="syncData?.translation?.is_complete"
            class="p-4 rounded-lg bg-green-50 border border-green-200"
          >
            <p class="text-green-700">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_COMPLETE_MSG')
              }}
            </p>
          </div>

          <!-- Trigger Translation Button -->
          <div v-if="canTriggerTranslation">
            <NextButton :is-loading="isTriggering" @click="triggerTranslation">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRIGGER_TRANSLATION_BTN')
              }}
            </NextButton>
            <p class="mt-2 text-sm text-slate-500">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRIGGER_TRANSLATION_HINT')
              }}
            </p>
          </div>

          <!-- Translation In Progress -->
          <div
            v-if="hasTranslationInProgress"
            class="p-4 rounded-lg bg-woot-50 border border-woot-200"
          >
            <p class="text-woot-700">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_IN_PROGRESS')
              }}
            </p>
          </div>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
